import React, { useEffect } from 'react'
import { getUserInfoSync } from '../../config/api'

const SideBar = ({ activeScreen, setActiveScreen, isFirstLogin }) => {
  const userInfo = getUserInfoSync()
  const firstLogin = isFirstLogin || userInfo?.first_login === true

  const handleMenuClick = (screen) => {
    // If first login, only allow profile screen
    if (firstLogin && screen !== 'profile') {
      return
    }
    setActiveScreen(screen)
  }

  const isActive = (screenOrScreens) => {
    if (Array.isArray(screenOrScreens)) {
      return screenOrScreens.includes(activeScreen) ? '' : 'collapsed'
    }
    return activeScreen === screenOrScreens ? '' : 'collapsed'
  }

  const isSubActive = (screen) => {
    return activeScreen === screen ? 'active' : ''
  }

  const isExpanded = (screens) => {
    return screens.includes(activeScreen) ? 'collapse show' : 'collapse'
  }

  // Ensure parent menus are expanded when a sub-item is active
  useEffect(() => {
    const menuMappings = {
      'sales-records': '#sales-nav',
      'daily-reconciliation': '#sales-nav',
      'cash-received-summary': '#sales-nav',
      'refunds-returns': '#sales-nav',
      'return-code': '#sales-nav',
      'quotations': '#sales-nav',
      'branch-stock': '#inventory-nav',
      'low-stock-alerts': '#inventory-nav',
      'incoming-transfers': '#stock-transfers-nav',
      'my-requests': '#stock-transfers-nav',
      'transfer-history': '#stock-transfers-nav',
      'add-expenses': '#expenses-nav',
      'manage-expenses': '#expenses-nav',
      'add-expense-category': '#expenses-nav',
      'manage-expense-category': '#expenses-nav',
      'expense-report': '#expenses-nav'
    }

    const targetId = menuMappings[activeScreen]
    if (targetId) {
      const collapseElement = document.querySelector(targetId)
      if (collapseElement && window.bootstrap && window.bootstrap.Collapse) {
        let bsCollapse = window.bootstrap.Collapse.getInstance(collapseElement)
        if (!bsCollapse) {
          bsCollapse = new window.bootstrap.Collapse(collapseElement, { toggle: false })
        }
        if (!collapseElement.classList.contains('show')) {
          bsCollapse.show()
        }
      }
    }
  }, [activeScreen])

  return (
    <aside id="sidebar" className="sidebar">
      <ul className="sidebar-nav" id="sidebar-nav">
        {/* Dashboard */}
        <li className="nav-item">
          <a 
            className={`nav-link ${isActive('dashboard')} ${firstLogin ? 'disabled' : ''}`}
            href="#"
            onClick={(e) => { e.preventDefault(); handleMenuClick('dashboard'); }}
            style={firstLogin ? { pointerEvents: 'none', opacity: 0.5, cursor: 'not-allowed' } : {}}
          >
            <i className="bi bi-grid" />
            <span>Dashboard</span>
          </a>
        </li>{/* End Dashboard Nav */}
        
        {/* Sales */}
        <li className="nav-item">
          <a 
            className={`nav-link ${isActive(['sales-records', 'daily-reconciliation', 'cash-received-summary', 'refunds-returns', 'return-code', 'quotations'])} ${firstLogin ? 'disabled' : ''}`}
            data-bs-target="#sales-nav" 
            data-bs-toggle={firstLogin ? '' : 'collapse'} 
            href="#"
            onClick={(e) => { e.preventDefault(); }}
            style={firstLogin ? { pointerEvents: 'none', opacity: 0.5, cursor: 'not-allowed' } : {}}
          >
            <i className="bi bi-cart" />
            <span>Sales</span>
            <i className="bi bi-chevron-down ms-auto" />
          </a>
          <ul id="sales-nav" className={`nav-content ${isExpanded(['sales-records', 'daily-reconciliation', 'cash-received-summary', 'refunds-returns', 'return-code', 'quotations'])}`} data-bs-parent="#sidebar-nav">
            <li>
              <a 
                href="#"
                onClick={(e) => { e.preventDefault(); handleMenuClick('sales-records'); }}
                className={isSubActive('sales-records')}
              >
                <i className="bi bi-circle" />
                <span>Sales Records</span>
              </a>
            </li>
            <li>
              <a 
                href="#"
                onClick={(e) => { e.preventDefault(); handleMenuClick('daily-reconciliation'); }}
                className={isSubActive('daily-reconciliation')}
              >
                <i className="bi bi-circle" />
                <span>Daily Reconciliation</span>
              </a>
            </li>
            <li>
              <a 
                href="#"
                onClick={(e) => { e.preventDefault(); handleMenuClick('cash-received-summary'); }}
                className={isSubActive('cash-received-summary')}
              >
                <i className="bi bi-circle" />
                <span>Cash Received Summary</span>
              </a>
            </li>
            
            <li>
              <a 
                href="#"
                onClick={(e) => { e.preventDefault(); handleMenuClick('return-code'); }}
                className={isSubActive('return-code')}
              >
                <i className="bi bi-circle" />
                <span>Return Code</span>
              </a>
            </li>
          </ul>
        </li>{/* End Sales Nav */}
        
        {/* Inventory */}
        <li className="nav-item">
          <a 
            className={`nav-link ${isActive(['branch-stock', 'low-stock-alerts'])} ${firstLogin ? 'disabled' : ''}`}
            data-bs-target="#inventory-nav" 
            data-bs-toggle={firstLogin ? '' : 'collapse'} 
            href="#"
            onClick={(e) => { e.preventDefault(); }}
            style={firstLogin ? { pointerEvents: 'none', opacity: 0.5, cursor: 'not-allowed' } : {}}
          >
            <i className="bi bi-box-seam" />
            <span>Inventory</span>
            <i className="bi bi-chevron-down ms-auto" />
          </a>
          <ul id="inventory-nav" className={`nav-content ${isExpanded(['branch-stock', 'low-stock-alerts'])}`} data-bs-parent="#sidebar-nav">
            <li>
              <a 
                href="#"
                onClick={(e) => { e.preventDefault(); handleMenuClick('branch-stock'); }}
                className={isSubActive('branch-stock')}
              >
                <i className="bi bi-circle" />
                <span>Branch Stock</span>
              </a>
            </li>
            <li>
              <a 
                href="#"
                onClick={(e) => { e.preventDefault(); handleMenuClick('low-stock-alerts'); }}
                className={isSubActive('low-stock-alerts')}
              >
                <i className="bi bi-circle" />
                <span>Low Stock Alerts</span>
              </a>
            </li>
          </ul>
        </li>{/* End Inventory Nav */}
        
        {/* Stock Transfers */}
        <li className="nav-item">
          <a 
            className={`nav-link ${isActive(['incoming-transfers', 'my-requests', 'transfer-history'])} ${firstLogin ? 'disabled' : ''}`}
            data-bs-target="#stock-transfers-nav" 
            data-bs-toggle={firstLogin ? '' : 'collapse'} 
            href="#"
            onClick={(e) => { e.preventDefault(); }}
            style={firstLogin ? { pointerEvents: 'none', opacity: 0.5, cursor: 'not-allowed' } : {}}
          >
            <i className="bi bi-arrow-left-right" />
            <span>Stock Transfers</span>
            <i className="bi bi-chevron-down ms-auto" />
          </a>
          <ul id="stock-transfers-nav" className={`nav-content ${isExpanded(['incoming-transfers', 'my-requests', 'transfer-history'])}`} data-bs-parent="#sidebar-nav">
            <li>
              <a 
                href="#"
                onClick={(e) => { e.preventDefault(); handleMenuClick('incoming-transfers'); }}
                className={isSubActive('incoming-transfers')}
              >
                <i className="bi bi-circle" />
                <span>Request Stock</span>
              </a>
            </li>
            <li>
              <a 
                href="#"
                onClick={(e) => { e.preventDefault(); handleMenuClick('my-requests'); }}
                className={isSubActive('my-requests')}
              >
                <i className="bi bi-circle" />
                <span>My Requests</span>
              </a>
            </li>
            <li>
              <a 
                href="#"
                onClick={(e) => { e.preventDefault(); handleMenuClick('transfer-history'); }}
                className={isSubActive('transfer-history')}
              >
                <i className="bi bi-circle" />
                <span>Transfer History</span>
              </a>
            </li>
          </ul>
        </li>{/* End Stock Transfers Nav */}
        
        {/* Expenses */}
        <li className="nav-item">
          <a 
            className={`nav-link ${isActive(['add-expenses', 'manage-expenses', 'add-expense-category', 'manage-expense-category', 'expense-report'])} ${firstLogin ? 'disabled' : ''}`}
            data-bs-target="#expenses-nav" 
            data-bs-toggle={firstLogin ? '' : 'collapse'} 
            href="#"
            onClick={(e) => { e.preventDefault(); }}
            style={firstLogin ? { pointerEvents: 'none', opacity: 0.5, cursor: 'not-allowed' } : {}}
          >
            <i className="bi bi-cash-stack" />
            <span>Expenses</span>
            <i className="bi bi-chevron-down ms-auto" />
          </a>
          <ul id="expenses-nav" className={`nav-content ${isExpanded(['add-expenses', 'manage-expenses', 'add-expense-category', 'manage-expense-category', 'expense-report'])}`} data-bs-parent="#sidebar-nav">
            <li>
              <a 
                href="#"
                onClick={(e) => { e.preventDefault(); handleMenuClick('add-expenses'); }}
                className={isSubActive('add-expenses')}
              >
                <i className="bi bi-circle" />
                <span>Add Expenses</span>
              </a>
            </li>
            <li>
              <a 
                href="#"
                onClick={(e) => { e.preventDefault(); handleMenuClick('manage-expenses'); }}
                className={isSubActive('manage-expenses')}
              >
                <i className="bi bi-circle" />
                <span>Manage Expenses</span>
              </a>
            </li>
            <li>
              <a 
                href="#"
                onClick={(e) => { e.preventDefault(); handleMenuClick('add-expense-category'); }}
                className={isSubActive('add-expense-category')}
              >
                <i className="bi bi-circle" />
                <span>Add Category</span>
              </a>
            </li>
            <li>
              <a 
                href="#"
                onClick={(e) => { e.preventDefault(); handleMenuClick('manage-expense-category'); }}
                className={isSubActive('manage-expense-category')}
              >
                <i className="bi bi-circle" />
                <span>Manage Expense Category</span>
              </a>
            </li>
            <li>
              <a 
                href="#"
                onClick={(e) => { e.preventDefault(); handleMenuClick('expense-report'); }}
                className={isSubActive('expense-report')}
              >
                <i className="bi bi-circle" />
                <span>Expense Report</span>
              </a>
            </li>
          </ul>
        </li>{/* End Expenses Nav */}
        
        {/* Reports */}
        <li className="nav-item">
          <a 
            className={`nav-link ${isActive('reports')} ${firstLogin ? 'disabled' : ''}`}
            href="#"
            onClick={(e) => { e.preventDefault(); handleMenuClick('reports'); }}
            style={firstLogin ? { pointerEvents: 'none', opacity: 0.5, cursor: 'not-allowed' } : {}}
          >
            <i className="bi bi-file-earmark-text" />
            <span>Reports</span>
          </a>
        </li>{/* End Reports Nav */}
        
        {/* Profile */}
        <li className="nav-item">
          <a 
            className={`nav-link ${isActive('profile')}`}
            href="#"
            onClick={(e) => { e.preventDefault(); handleMenuClick('profile'); }}
          >
            <i className="bi bi-person" />
            <span>Profile</span>
          </a>
        </li>{/* End Profile Nav */}
      </ul>
    </aside>
  )
}

export default SideBar
